import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Tag, Truck, Store, Box, Calendar as CalendarIcon, X } from 'lucide-react';
import { format as fmtDate } from 'date-fns';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const empty = {
  code: '', scope: 'delivery', vendor_id: '', product_id: '',
  discount_type: 'percent', discount_value: 10,
  min_subtotal_cad: 0, max_discount_cad: null,
  description: '', active: true, expires_at: '', usage_limit: null,
};

const scopeMeta = {
  delivery: { icon: Truck, color: 'bg-emerald-100 text-emerald-700' },
  vendor: { icon: Store, color: 'bg-blue-100 text-blue-700' },
  product: { icon: Box, color: 'bg-amber-100 text-amber-700' },
};

const PromotionsPage = () => {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    try {
      const [pr, vd] = await Promise.all([
        api.get('/admin/promotions').then((r) => r.data),
        api.get('/admin/vendors').then((r) => r.data),
      ]);
      setItems(pr);
      setVendors(vd);
    } catch (e) { /* */ }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty }); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...p,
      expires_at: p.expires_at || '',  // already ISO
      usage_limit: p.usage_limit ?? null,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.code) { toast.error('Code is required'); return; }
    if (form.scope === 'vendor' && !form.vendor_id) { toast.error('Choose a vendor'); return; }
    if (form.scope === 'product' && (!form.vendor_id || !form.product_id)) {
      toast.error('Vendor + product id required'); return;
    }
    const body = { ...form };
    if (!body.expires_at) delete body.expires_at;
    // expires_at is already ISO from the calendar popover
    if (!body.usage_limit) body.usage_limit = null;
    if (!body.max_discount_cad) body.max_discount_cad = null;
    body.discount_value = Number(body.discount_value);
    body.min_subtotal_cad = Number(body.min_subtotal_cad || 0);
    try {
      if (editing) {
        const patch = { ...body };
        delete patch.scope; delete patch.vendor_id; delete patch.product_id; delete patch.created_at;
        await api.patch(`/admin/promotions/${editing.id}`, patch);
        toast.success('Promotion updated');
      } else {
        await api.post('/admin/promotions', body);
        toast.success('Promotion created');
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete promotion ${p.code}?`)) return;
    try { await api.delete(`/admin/promotions/${p.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const filtered = filter === 'all' ? items : items.filter((p) => p.scope === filter);

  return (
    <div data-testid="promotions-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Tag className="h-6 w-6 text-emerald-600" /> Promotions
          </h1>
          <p className="text-slate-500 mt-1 max-w-2xl">
            Create delivery-fee discount codes (global). Store-wide and product discounts are managed by each vendor in their own portal.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-promotion-btn">
              <Plus className="h-4 w-4 mr-1.5" />New promotion
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? `Edit ${editing.code}` : 'Create promotion'}</DialogTitle></DialogHeader>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Code</Label>
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SUMMER10" className="h-10 mt-1" data-testid="promo-code-input" />
                </div>
                <div>
                  <Label>Scope</Label>
                  <select value="delivery" disabled
                    className="h-10 mt-1 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-sm"
                    data-testid="promo-scope-select">
                    <option value="delivery">Delivery fee (global)</option>
                  </select>
                  <p className="text-[10px] text-slate-500 mt-1">Store-wide & product discounts are created by each vendor in their portal.</p>
                </div>
              </div>

              {(form.scope === 'vendor' || form.scope === 'product') && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Vendor</Label>
                    <select value={form.vendor_id} disabled={!!editing}
                      onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
                      className="h-10 mt-1 w-full rounded-md border border-slate-200 bg-white px-2 text-sm disabled:bg-slate-50"
                      data-testid="promo-vendor-select">
                      <option value="">Select…</option>
                      {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  </div>
                  {form.scope === 'product' && (
                    <div>
                      <Label>Product ID</Label>
                      <Input value={form.product_id || ''} onChange={(e) => setForm({ ...form, product_id: e.target.value })} placeholder="menu-item id" className="h-10 mt-1" />
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Discount type</Label>
                  <select value={form.discount_type}
                    onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                    className="h-10 mt-1 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                    <option value="percent">Percent (%)</option>
                    <option value="flat">Flat (CAD)</option>
                  </select>
                </div>
                <div>
                  <Label>Value</Label>
                  <Input type="number" step="0.5" value={form.discount_value}
                    onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="h-10 mt-1" data-testid="promo-value-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Min subtotal (CAD)</Label>
                  <Input type="number" step="0.5" value={form.min_subtotal_cad || 0}
                    onChange={(e) => setForm({ ...form, min_subtotal_cad: e.target.value })} className="h-10 mt-1" />
                </div>
                <div>
                  <Label>Max discount (CAD, optional)</Label>
                  <Input type="number" step="0.5" value={form.max_discount_cad || ''}
                    onChange={(e) => setForm({ ...form, max_discount_cad: e.target.value || null })} placeholder="No cap" className="h-10 mt-1" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Expires at (optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" type="button"
                        className="h-10 mt-1 w-full justify-start text-left font-normal text-slate-800"
                        data-testid="expires-at-btn">
                        <CalendarIcon className="h-4 w-4 mr-2 text-slate-500" />
                        {form.expires_at
                          ? fmtDate(new Date(form.expires_at), 'MMM d, yyyy — h:mm a')
                          : <span className="text-slate-400">Never expires</span>}
                        {form.expires_at && (
                          <X className="h-3.5 w-3.5 ml-auto text-slate-400 hover:text-rose-500"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setForm({ ...form, expires_at: '' }); }}
                          />
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-0" data-testid="expires-at-popover">
                      <Calendar
                        mode="single"
                        selected={form.expires_at ? new Date(form.expires_at) : undefined}
                        onSelect={(d) => {
                          if (!d) { setForm({ ...form, expires_at: '' }); return; }
                          // Preserve any prior time-of-day (default 23:59).
                          const prev = form.expires_at ? new Date(form.expires_at) : null;
                          const h = prev ? prev.getHours() : 23;
                          const m = prev ? prev.getMinutes() : 59;
                          const next = new Date(d);
                          next.setHours(h, m, 0, 0);
                          setForm({ ...form, expires_at: next.toISOString() });
                        }}
                        initialFocus
                      />
                      <div className="border-t border-slate-200 p-3 flex items-center gap-2">
                        <Label className="text-xs text-slate-600 mr-1">Time</Label>
                        <Input
                          type="time"
                          value={form.expires_at ? fmtDate(new Date(form.expires_at), 'HH:mm') : '23:59'}
                          onChange={(e) => {
                            const [h, m] = (e.target.value || '23:59').split(':').map(Number);
                            const base = form.expires_at ? new Date(form.expires_at) : new Date();
                            base.setHours(h || 0, m || 0, 0, 0);
                            setForm({ ...form, expires_at: base.toISOString() });
                          }}
                          className="h-9 w-28"
                          data-testid="expires-at-time"
                        />
                        <Button type="button" size="sm" variant="outline" className="ml-auto"
                          onClick={() => setForm({ ...form, expires_at: '' })}
                          data-testid="expires-at-clear">Clear</Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>Usage limit (optional)</Label>
                  <Input type="number" value={form.usage_limit || ''}
                    onChange={(e) => setForm({ ...form, usage_limit: e.target.value || null })} placeholder="Unlimited" className="h-10 mt-1" />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Input value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="e.g. 10% off delivery for new users" className="h-10 mt-1" />
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700 mt-2">
                <input type="checkbox" checked={!!form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Active
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-promotion-btn">
                {editing ? 'Save' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-5 flex gap-2 flex-wrap">
        {['all', 'delivery', 'vendor', 'product'].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 h-8 rounded-full text-sm font-medium ${filter === s ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            {s[0].toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-12 bg-white rounded-2xl border border-slate-200 border-dashed">
            No promotions yet. Click <span className="font-medium">New promotion</span> to create one.
          </div>
        )}
        {filtered.map((p) => {
          const Meta = scopeMeta[p.scope] || scopeMeta.delivery;
          const Icon = Meta.icon;
          const vname = vendors.find((v) => v.id === p.vendor_id)?.name;
          return (
            <div key={p.id} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`promo-card-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-medium ${Meta.color}`}>
                  <Icon className="h-3.5 w-3.5" />{p.scope}
                </div>
                {!p.active && <Badge className="bg-slate-200 text-slate-600 hover:bg-slate-200">Inactive</Badge>}
              </div>
              <div className="mt-2 text-xl font-mono font-semibold text-slate-900">{p.code}</div>
              <div className="text-sm text-slate-500">{p.description || '—'}</div>
              <div className="mt-3 text-sm text-slate-700">
                {p.discount_type === 'percent' ? `${p.discount_value}% off` : `CAD ${Number(p.discount_value).toFixed(2)} off`}
                {' '}{p.scope === 'delivery' ? 'delivery fee' : 'subtotal'}
              </div>
              {vname && <div className="text-xs text-slate-500 mt-1">Vendor: {vname}</div>}
              <div className="mt-2 text-xs text-slate-400">
                Used {p.used_count || 0}{p.usage_limit ? ` / ${p.usage_limit}` : ''} • Min CAD {Number(p.min_subtotal_cad || 0).toFixed(2)}
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-promo-${p.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                <Button size="sm" variant="outline" onClick={() => remove(p)} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`delete-promo-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PromotionsPage;
